import { authService } from './auth';
import { downloadBlob } from './api';
import type { TTSOptions } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

export interface TTSGeneratePassageInput {
  passage_id: string;
  passage: string;
}

export interface TTSGenerateRequest {
  passages: TTSGeneratePassageInput[];
  options: Pick<TTSOptions, 'voice' | 'speed' | 'instructions'>;
  workbook_title?: string;
}

export interface TTSDownloadResult {
  successCount: number;
  failedCount: number;
  creditsCharged: number | null;
}

function getAuthHeaders(): HeadersInit {
  const token = authService.getToken();
  if (!token) {
    throw new Error('로그인이 필요합니다.');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function extractFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;

  // RFC 5987 format: filename*=UTF-8''encoded_name
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through
    }
  }

  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].replace(/['"]/g, '');
  }

  return fallback;
}

export async function generateTtsAndDownload(
  request: TTSGenerateRequest,
  signal?: AbortSignal
): Promise<TTSDownloadResult> {
  const response = await fetch(`${API_BASE_URL}/api/tts/generate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('로그인이 필요합니다.');
    }
    const error = await response.json().catch(() => null);
    const message =
      (error && (error.message || error.error)) ||
      `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(message);
  }

  const contentType = response.headers.get('content-type') || '';
  const fallbackName =
    request.passages.length > 1
      ? `${request.workbook_title || 'workbook'}.zip`
      : `${request.passages[0]?.passage_id || 'passage'}.mp3`;

  const filename = extractFilename(
    response.headers.get('content-disposition'),
    fallbackName
  );

  const blob = await response.blob();
  // Abort may race with an already-fulfilled body promise. Do not download
  // a previous session's artifact merely because body reading succeeded.
  signal?.throwIfAborted();
  if (blob.size === 0) {
    throw new Error('음성파일이 비어 있습니다. 다시 시도해 주세요.');
  }

  // Use content-type if available (helps Safari)
  const typedBlob = contentType
    ? new Blob([blob], { type: contentType })
    : blob;
  downloadBlob(typedBlob, filename);
  const countHeader = (name: string, fallback: number): number => {
    const raw = response.headers.get(name);
    if (raw === null) return fallback;
    const value = Number(raw);
    return Number.isInteger(value) && value >= 0 ? value : fallback;
  };
  return {
    successCount: countHeader('X-TTS-Success-Count', request.passages.length),
    failedCount: countHeader('X-TTS-Failed-Count', 0),
    creditsCharged: response.headers.has('X-TTS-Credits-Charged')
      ? countHeader('X-TTS-Credits-Charged', 0)
      : null,
  };
}
